/**
 * Projects + graph links → a scope's repository (git sync Stage 2, S2.6): one
 * YAML per project with every link that touches it, and `projects/links.yaml`
 * for the drawn links between two other items. A project only REFERENCES its
 * members (graph: "a reference holder"), so its file holds ids — the members
 * themselves are documents / tasks elsewhere in the repository (or not in it
 * at all, then the file says so).
 */
import { listProjects, listLinks, listRepos } from '../../graph/projectsStore';
import { registerScopeContributor } from './scopeRepo';
import { reportScopeProgress } from './scopeProgress';
import { fingerprint } from './scopeDocumentsLayout';
import { projectPath, projectYaml, linksYaml, LINKS_FILE } from './scopeProjectsLayout';

/** What the scope's projects and links were when last written — a pull compares (edits made meanwhile are kept). */
export const exportedProjects = new Map();
/* eslint-disable no-unused-vars */
export const projectsMark = (projects, links, repos = []) =>
  fingerprint(
    JSON.stringify([
      projects.map(({ updated_at, scopeKey, ...p }) => p),
      links.map((l) => l.id).sort(),
      repos.map(({ updated_at, scopeKey, ...r }) => r).sort((a, b) => String(a.id).localeCompare(String(b.id))),
    ])
  );
/* eslint-enable no-unused-vars */

/** The repositories a project links (its "contains → git_repo:" links), by id. */
export function reposOf(projectId, links, repos) {
  const node = `project:${projectId}`;
  const ids = new Set(links.filter((l) => l.from === node && l.rel === 'contains' && l.to.startsWith('git_repo:')).map((l) => l.to.slice('git_repo:'.length)));
  return repos.filter((r) => ids.has(String(r.id)));
}

export async function projectFiles(scopeKey) {
  reportScopeProgress(scopeKey, { step: 'projects' });
  const projects = await listProjects(scopeKey);
  const links = await listLinks(scopeKey);
  const repos = await listRepos(scopeKey);
  const files = {};
  const projectNodes = new Set(projects.map((p) => `project:${p.id}`));
  // A local-folder repository is not written (it stays on this device), so neither is its link.
  const localOnly = new Set(repos.filter((r) => !r.remote).map((r) => `git_repo:${r.id}`));
  const shared = links.filter((l) => !localOnly.has(l.to));
  for (const p of projects) {
    const node = `project:${p.id}`;
    files[projectPath(p)] = projectYaml(p, shared.filter((l) => l.from === node || l.to === node), reposOf(p.id, shared, repos));
  }
  const others = shared.filter((l) => !projectNodes.has(l.from) && !projectNodes.has(l.to));
  if (others.length) files[LINKS_FILE] = linksYaml(others);
  exportedProjects.set(scopeKey, projectsMark(projects, links, repos));
  return { files };
}

let registered = null;
export function registerProjectsContributor() {
  if (!registered) {
    registered = registerScopeContributor({
      id: 'projects',
      files: async (scopeKey) => (await projectFiles(scopeKey)).files,
      apply: async (scopeKey, change) => (await import('./scopeProjectsApply')).applyProjectChanges(scopeKey, change),
    });
  }
  return registered;
}
